import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const articles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-24 px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-800">分类不存在</h1>
            <p className="mt-3 text-foreground-500">您访问的分类页面不存在或已被移除。</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id': `http://lalaccu.cn/category/${category.slug}`,
        url: `http://lalaccu.cn/category/${category.slug}`,
        name: `${category.name} - 51app`,
        description: category.description,
        isPartOf: { '@id': 'http://lalaccu.cn/' },
        about: {
          '@type': 'Thing',
          name: category.name,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'http://lalaccu.cn/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: category.name,
            item: `http://lalaccu.cn/category/${category.slug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 51app | 全网吃瓜爆料`}
        description={`${category.description}。51app ${category.name}频道汇集全网最热门的${category.name}内容，免费浏览所有爆料资讯。51app下载，免费app，今日最热吃瓜。`}
        keywords={`${category.name},51app,吃瓜爆料,免费app,${category.name}最新,${category.name}爆料,51app下载`}
        canonical={`/category/${category.slug}`}
        schema={schema}
      />
      <Navbar />
      <main className="pt-24 md:pt-28">
        <div className="px-4 md:px-8 max-w-6xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={[{ label: category.name }]} />
          </div>

          <div className="mb-8 md:mb-10">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center">
                <i className={`${category.icon} w-6 h-6 flex items-center justify-center text-background-50`} />
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-primary-500">{category.name}</h1>
                <p className="text-sm text-foreground-500">{category.articleCount} 篇爆料 · 持续更新</p>
              </div>
            </div>
            <p className="text-base text-foreground-600 max-w-2xl">{category.description}</p>
          </div>

          {articles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-foreground-500">该分类暂无爆料内容，敬请期待。</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 pb-16">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="group rounded-xl bg-background-100 overflow-hidden hover:bg-background-200/70 transition-colors"
                  itemScope
                  itemType="https://schema.org/Article"
                >
                  <Link to={`/article/${article.id}`} className="flex flex-col h-full">
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        itemProp="image"
                      />
                      {article.isHot && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-medium">
                          <i className="ri-fire-line w-3 h-3 flex items-center justify-center" />
                          最热
                        </span>
                      )}
                      {article.isNew && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                          <i className="ri-flashlight-line w-3 h-3 flex items-center justify-center" />
                          最新
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5 flex flex-col flex-1">
                      <h2
                        className="font-heading text-base md:text-lg text-foreground-800 leading-snug group-hover:text-primary-500 transition-colors line-clamp-2"
                        itemProp="headline"
                      >
                        {article.title}
                      </h2>
                      <p className="mt-2 text-sm text-foreground-500 line-clamp-2" itemProp="description">
                        {article.summary}
                      </p>
                      <div className="mt-auto pt-3 flex items-center gap-2 text-xs text-foreground-400">
                        <span itemProp="author" itemScope itemType="https://schema.org/Person">
                          <span itemProp="name">{article.author}</span>
                        </span>
                        <span>·</span>
                        <time itemProp="datePublished" dateTime={article.date}>
                          {article.date}
                        </time>
                        <span>·</span>
                        <span>{article.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}